import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  GitBranch,
  Lock,
  Globe,
  Star,
  GitFork,
  AlertCircle,
  Users,
  Building2,
  ExternalLink,
} from 'lucide-react';
import { formatNumber } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { cn } from '@/lib/utils';

const REFETCH = 30_000;

export function GiteaWidget() {
  const now = useNow(1_000);
  const q = useQuery({
    queryKey: ['gitea'],
    queryFn: endpoints.integrations.gitea,
    refetchInterval: REFETCH,
  });
  const data = q.data?.gitea;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="h-3.5 w-3.5" />
          Gitea
        </CardTitle>
        {data && (
          <span className="text-xs text-muted-foreground">v{data.version}</span>
        )}
      </CardHeader>
      <CardBody>
        {!data ? (
          <div className="text-sm text-muted-foreground">
            {q.isLoading ? 'loading…' : 'unavailable (check GITEA_BASE_URL env)'}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <Tile
                label="users"
                value={data.totalUsers !== null ? formatNumber(data.totalUsers) : '—'}
                icon={Users}
                muted={data.totalUsers === null}
              />
              <Tile
                label="orgs"
                value={data.totalOrgs !== null ? formatNumber(data.totalOrgs) : '—'}
                icon={Building2}
                muted={data.totalOrgs === null}
              />
              <Tile
                label="recent"
                value={formatNumber(data.recentRepos.length)}
                icon={GitBranch}
                hint="top of search"
              />
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                Recent activity
              </div>
              {data.recentRepos.length === 0 ? (
                <div className="text-xs text-muted-foreground">no repos found</div>
              ) : (
                <ul className="space-y-1.5">
                  {data.recentRepos.slice(0, 8).map((r) => (
                    <li key={r.id} className="text-xs">
                      <a
                        href={r.htmlUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="group flex items-center gap-1.5 hover:bg-accent/40 rounded px-1.5 py-1 -mx-1.5"
                      >
                        {r.private ? (
                          <Lock className="h-3 w-3 text-amber-400/70 shrink-0" />
                        ) : (
                          <Globe className="h-3 w-3 text-muted-foreground shrink-0" />
                        )}
                        <span className="font-mono text-foreground/90 truncate flex-1 min-w-0">
                          {r.fullName}
                        </span>
                        <span className="flex items-center gap-2 text-muted-foreground shrink-0">
                          {r.stars > 0 && (
                            <span className="flex items-center gap-0.5 tabular-nums">
                              <Star className="h-2.5 w-2.5" />
                              {r.stars}
                            </span>
                          )}
                          {r.forks > 0 && (
                            <span className="flex items-center gap-0.5 tabular-nums">
                              <GitFork className="h-2.5 w-2.5" />
                              {r.forks}
                            </span>
                          )}
                          {r.openIssues > 0 && (
                            <span className="flex items-center gap-0.5 tabular-nums text-amber-400/70">
                              <AlertCircle className="h-2.5 w-2.5" />
                              {r.openIssues}
                            </span>
                          )}
                        </span>
                      </a>
                      {r.description && (
                        <p className="text-[10px] text-muted-foreground/70 truncate pl-5 mt-0.5">
                          {r.description}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex items-center justify-end text-xs text-muted-foreground border-t border-border pt-2">
              <a
                href={process.env.GITEA_BASE_URL || 'http://192.168.1.61'}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center gap-1 hover:text-foreground"
              >
                open Gitea <ExternalLink className="h-3 w-3" />
              </a>
              <span className="mx-2">·</span>
              <span>{q.data?.lastUpdated ? relTime(q.data.lastUpdated, now) : '—'}</span>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Tile({
  label,
  value,
  icon: Icon,
  hint,
  muted,
}: {
  label: string;
  value: string;
  icon: typeof GitBranch;
  hint?: string;
  muted?: boolean;
}) {
  return (
    <div className="rounded-md border border-border p-2">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
        {hint && <span className="text-[9px] opacity-60">({hint})</span>}
      </div>
      <div
        className={cn(
          'text-lg font-semibold tabular-nums mt-0.5',
          muted && 'text-muted-foreground/50',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function relTime(iso: string, now: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const diff = Math.max(0, Math.floor((now - t) / 1000));
  if (diff === 0) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}
